import React, { useState, useEffect, useRef } from 'react';
import GrantitudeSpiral from './GrantitudeSpiral';

/**
 * CosmicArtInstallation - A beautiful layered art experience
 * 
 * Combines:
 * 1. Slow-moving Grant spiral orb lava lamp in the background
 * 2. Semi-transparent rotating art images
 * 3. Smooth crossfade transitions (no jumping)
 * 
 * Creates a trippy, meditative, art installation experience
 */

// Beautiful contemplation images from the Belonging Sessions collection
const CONTEMPLATION_IMAGES = [
  {
    url: 'https://customer-assets.emergentagent.com/job_elchme-hub/artifacts/85pgeaxi_C830348F-EE4D-4D53-8BC5-4132CB4FE5A2.jpeg',
    name: 'Golden Tree of Belonging',
    mood: 'grounding'
  },
  {
    url: 'https://customer-assets.emergentagent.com/job_elchme-hub/artifacts/yl4e1hi1_ChatGPT%20Image%20Oct%2011%2C%202025%20at%2012_38_50%20PM.jpeg',
    name: 'Cosmic Spiral',
    mood: 'expansion'
  },
  {
    url: 'https://customer-assets.emergentagent.com/job_elchme-hub/artifacts/811qlt4v_D09C54E9-7DCA-4C17-AA81-819FB4F1D418.jpeg',
    name: 'Emerald Consciousness',
    mood: 'connection'
  },
  {
    url: 'https://customer-assets.emergentagent.com/job_elchme-hub/artifacts/073wpn1t_ChatGPT%20Image%20Oct%2011%2C%202025%20at%2012_38_53%20PM.jpeg',
    name: 'Solar Mandala',
    mood: 'awakening'
  },
  {
    url: 'https://customer-assets.emergentagent.com/job_elchme-hub/artifacts/lvyqwsmw_ChatGPT%20Image%20Oct%2012%2C%202025%20at%2002_44_30%20AM.jpeg',
    name: 'Golden Meditation',
    mood: 'peace'
  }
];

// Image layer component with smooth fade transitions
const ImageLayer = ({ image, isVisible, baseOpacity = 0.35 }) => (
  <div
    className="absolute inset-0"
    style={{
      backgroundImage: `url(${image.url})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      opacity: isVisible ? baseOpacity : 0,
      transition: 'opacity 3000ms ease-in-out',
      willChange: 'opacity',
    }}
    aria-label={image.name}
  />
);

/**
 * Full-screen cosmic art installation
 * Use for meditation, breathing, and contemplation moments
 */
export const CosmicArtInstallation = ({
  mode = 'full', // 'full' | 'background' | 'compact'
  intensity = 1,
  showSpiral = true,
  imageOpacity = 0.35,
  transitionDuration = 12000, // ms between image changes
  breathSync = null,
  className = '',
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [mounted, setMounted] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  // Smooth image rotation - simply change the active index
  useEffect(() => {
    if (!mounted) return;

    intervalRef.current = setInterval(() => {
      setActiveIndex(prev => (prev + 1) % CONTEMPLATION_IMAGES.length);
    }, transitionDuration);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [mounted, transitionDuration]);

  const sizeClasses = {
    full: 'fixed inset-0',
    background: 'absolute inset-0',
    compact: 'relative w-full h-full min-h-[400px]'
  };

  return (
    <div 
      className={`${sizeClasses[mode]} overflow-hidden ${className}`}
      data-testid="cosmic-art-installation"
      style={{
        background: 'radial-gradient(ellipse at center, #0a0a1a 0%, #080A0E 100%)'
      }}
    >
      {/* Layer 1: Grant Spiral Orb - fills entire background, subtle and fractal */}
      {showSpiral && (
        <div 
          className="absolute inset-0 flex items-center justify-center"
          style={{ 
            opacity: 0.6,
          }}
        >
          <GrantitudeSpiral 
            size={Math.max(typeof window !== 'undefined' ? window.innerWidth : 1920, typeof window !== 'undefined' ? window.innerHeight : 1080) * 1.5}
            animate={true}
            mode="background"
            intensity={intensity * 0.8}
            breathSync={breathSync}
          />
        </div>
      )}

      {/* Layer 2: All images stacked - only active one visible via opacity */}
      {CONTEMPLATION_IMAGES.map((image, index) => (
        <ImageLayer 
          key={image.url}
          image={image}
          isVisible={index === activeIndex}
          baseOpacity={imageOpacity * intensity}
        />
      ))}

      {/* Layer 3: Subtle gradient overlay for depth */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          background: `
            radial-gradient(ellipse at 30% 30%, rgba(245, 158, 11, 0.05) 0%, transparent 50%),
            radial-gradient(ellipse at 70% 70%, rgba(16, 185, 129, 0.05) 0%, transparent 50%),
            radial-gradient(ellipse at center, transparent 30%, rgba(8, 10, 14, 0.3) 100%)
          `
        }}
      />

      {/* Layer 4: Vignette for immersion */}
      <div 
        className="absolute inset-0 pointer-events-none"
        style={{
          boxShadow: 'inset 0 0 150px 50px rgba(0,0,0,0.4)'
        }}
      />
    </div>
  );
};

/**
 * Compact version for inline use in cards/panels
 */
export const CosmicArtCard = ({ 
  size = 300, 
  imageOpacity = 0.4,
  className = '' 
}) => (
  <div 
    className={`relative rounded-2xl overflow-hidden ${className}`}
    style={{ width: size, height: size }}
  >
    <CosmicArtInstallation 
      mode="compact"
      imageOpacity={imageOpacity}
      transitionDuration={15000}
    />
  </div>
);

/**
 * Background layer for pages - very subtle
 */
export const CosmicBackground = ({ intensity = 0.3 }) => (
  <CosmicArtInstallation 
    mode="background"
    intensity={intensity}
    imageOpacity={0.15}
    transitionDuration={20000}
  />
);

/**
 * Hook to get random contemplation image
 */
export const useContemplationImage = () => {
  const [image, setImage] = useState(CONTEMPLATION_IMAGES[0]);
  
  const getRandomImage = () => {
    const randomIndex = Math.floor(Math.random() * CONTEMPLATION_IMAGES.length);
    setImage(CONTEMPLATION_IMAGES[randomIndex]);
  };
  
  return { image, getRandomImage, allImages: CONTEMPLATION_IMAGES };
};

export { CONTEMPLATION_IMAGES };
export default CosmicArtInstallation;
